import dayjs from "dayjs";
import { FC, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetParticipantDetailsById } from "../../../../../../../../../../api/services/program/programApis";
import {
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiTextField,
} from "../../../../../../../../../../components";
import { STATUS_BADGE_TYPE } from "../../../../../../../../../../components/statusBadge/StatusBadge";
import { MAX_LENGTH_1000 } from "../../../../../../../../../../helpers/constants";
import {
  getTomorrowDate,
  isNotEmpty,
} from "../../../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../../../redux/slices/appState/appStateSlice";
import { programStatusSelector } from "../../../../../../../../../../redux/slices/program/programSelector";
import { ProgramStatusFormKey } from "../../../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../../siteCore/SiteCoreKeys";

interface ChangStatusFieldsProps {
  pageId: string;
  isEditInactivateProgram?: boolean;
  userCountryDateFormat?: string;
  showEditForReopen?: boolean;
  handleErrorReset?: () => void;
  isEditProgram?: boolean;
  programId: string;
}

export const ChangStatusFields: FC<ChangStatusFieldsProps> = ({
  pageId,
  isEditInactivateProgram = false,
  userCountryDateFormat = "",
  showEditForReopen = false,
  handleErrorReset,
  isEditProgram,
  programId,
}) => {
  const { control, getValues, watch } = useFormContext();
  const gridSize = { md: 6, sm: 6, xs: 12 };
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const programStatus = useSelector(programStatusSelector);

  const {
    data: editParticipantData,
    isFetching: isPending,
    isError: isProgramError,
    refetchWithParams: getParticipantDetailsByProgramId,
  } = useGetParticipantDetailsById();

  useEffect(() => {
    if (isProgramError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isProgramError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        pendingLoader: {
          isVisible: isPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ pendingLoader: { isVisible: false } }));
    };
  }, [isPending]);

  useEffect(() => {
    if (programId && isEditProgram) {
      getParticipantDetailsByProgramId(programId);
    }
  }, [programId, isEditProgram, programStatus]);

  const programStatusWatch = useWatch({
    control,
    name: ProgramStatusFormKey.programStatus,
  });

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.programStatusKey, key);
  };
  const getPopupLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.changeStatusModelPopupKey, key);
  };

  const showThroughDate = useMemo(() => {
    const { fromDate, programStatus } = getValues();
    if (isNotEmpty(fromDate) && programStatus === STATUS_BADGE_TYPE.inActive) {
      return true;
    }
    return false;
  }, [watch(ProgramStatusFormKey.fromDate)]);

  // for check inactive scenario
  const isProgramInactive = useMemo(() => {
    const { programStatus } = getValues();
    return programStatus === STATUS_BADGE_TYPE.inActive;
  }, [programStatusWatch]);

  const isReOpen = useMemo(() => {
    const { programStatus } = getValues();
    return programStatus === STATUS_BADGE_TYPE.reOpen;
  }, [programStatusWatch]);

  const tomorrowDate = getTomorrowDate();

  const maxFromDate = useMemo(() => {
    if (editParticipantData?.projectedProgramEndDate) {
      // Parse ISO date and subtract 1 day to ensure from date is strictly less than projectedProgramEndDate
      const maxDate = dayjs(
        editParticipantData.projectedProgramEndDate,
      ).subtract(1, "day");
      return maxDate;
    }
    return undefined;
  }, [editParticipantData?.projectedProgramEndDate]);

  return (
    <MuiBox className="change-status-form" data-testid="change-status-form">
      <MuiGrid container className="form-container">
        <MuiGrid size={gridSize} className="status-field">
          <MuiDateFieldFormController
            control={control}
            name={ProgramStatusFormKey.fromDate}
            className="date-picker"
            label={getPopupLabel(programSiteCoreKeys.fromDate)}
            id={`${pageId}-${ProgramStatusFormKey.fromDate}`}
            disableManualInput
            format={userCountryDateFormat}
            onChange={handleErrorReset}
            disablePast
            maxDate={maxFromDate}
            isMandatory={isProgramInactive || isReOpen || showEditForReopen}
            value={
              getValues(ProgramStatusFormKey.fromDate) !== ""
                ? dayjs(
                    getValues(ProgramStatusFormKey.fromDate),
                    userCountryDateFormat,
                  )
                : null
            }
            disableLabel={isEditInactivateProgram}
            disabled={isEditInactivateProgram}
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
          />
        </MuiGrid>
        {showThroughDate && (
          <MuiGrid size={gridSize} className="status-field">
            <MuiDateFieldFormController
              control={control}
              name={ProgramStatusFormKey.throughDate}
              className="date-picker"
              label={getPopupLabel(programSiteCoreKeys.throughDate)}
              id={`${pageId}-${ProgramStatusFormKey.throughDate}`}
              isMandatory={isProgramInactive}
              disableManualInput
              format={userCountryDateFormat}
              onChange={handleErrorReset}
              value={
                getValues(ProgramStatusFormKey.throughDate) !== ""
                  ? dayjs(
                      getValues(ProgramStatusFormKey.throughDate),
                      userCountryDateFormat,
                    )
                  : null
              }
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
            />
          </MuiGrid>
        )}

        {(isReOpen || showEditForReopen) && (
          <MuiGrid size={gridSize} className="status-field">
            <MuiDateFieldFormController
              control={control}
              name={ProgramStatusFormKey.projectedProgramEndDate}
              label={getPopupLabel(
                programSiteCoreKeys.projectedProgramEndDateLabel,
              )}
              format={userCountryDateFormat}
              className="date-picker"
              minDate={tomorrowDate}
              isMandatory
              id={`${pageId}-${ProgramStatusFormKey.projectedProgramEndDate}`}
              disableManualInput
              value={
                getValues(ProgramStatusFormKey.projectedProgramEndDate) !== ""
                  ? dayjs(
                      getValues(ProgramStatusFormKey.projectedProgramEndDate),
                      userCountryDateFormat,
                    )
                  : null
              }
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
            />
          </MuiGrid>
        )}

        <MuiGrid size={{ md: 8, sm: 8, xs: 12 }} className="status-field">
          <FormController control={control} name={ProgramStatusFormKey.notes}>
            <MuiTextField
              id={`${pageId}-${ProgramStatusFormKey.notes}`}
              label={getLabel(programSiteCoreKeys.notes)}
              placeholder={commonLabels.inputPlaceHolder}
              isTextarea
              multiline
              rows={4}
              maxLength={MAX_LENGTH_1000}
              requiredARIALabel={commonARIALabel.required}
              isMandatory={isReOpen}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
